import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

// Bbox aproximado de Saltillo / Ramos Arizpe
const DEFAULT_BBOX = [-101.15, 25.28, -100.78, 25.56];

/**
 * Selector de dirección con mini-mapa Mapbox.
 * Props:
 *  - value: { address, lat, lng, colonia } | null
 *  - onChange(next): callback con el destino elegido (o null si se borró)
 *  - center: [lng, lat] centro inicial del mapa (default Saltillo)
 *  - height: alto del mapa en px (default 260)
 */
export default function AddressPicker({ value, onChange, center = [-100.9649, 25.4386], height = 260 }) {
  const mapContainer = useRef(null);
  const geocoderContainer = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const initedRef = useRef(false);

  useEffect(() => {
    if (initedRef.current) return;
    if (!MAPBOX_TOKEN) return;

    const setupGeocoder = (map) => {
      // Helper: colocar/mover pin en un punto y hacer reverse-geocoding para
      // obtener la calle y colonia del nuevo lugar.
      const placePinAt = async (lng, lat) => {
        // Pintar marker draggable
        if (markerRef.current) markerRef.current.remove();
        const el = document.createElement('div');
        el.style.cssText = 'width:30px;height:30px;background:#2563eb;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:12px;cursor:grab;';
        el.textContent = '📍';
        const marker = new mapboxgl.Marker({ element: el, draggable: true })
          .setLngLat([lng, lat])
          .addTo(map);
        markerRef.current = marker;

        // Reverse geocode: pedir a Mapbox el nombre de la calle+colonia en ese punto
        const reverseAndReport = async (finalLng, finalLat) => {
          try {
            const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${finalLng},${finalLat}.json?language=es&types=address,poi,neighborhood,locality&access_token=${MAPBOX_TOKEN}`;
            const res = await fetch(url);
            const data = await res.json();
            const feat = data?.features?.[0];
            let colonia = '';
            let address = '';
            if (feat) {
              address = feat.place_name || '';
              const pt = Array.isArray(feat.place_type) ? feat.place_type : [];
              if (pt.includes('neighborhood') || pt.includes('locality')) colonia = feat.text || '';
              if (Array.isArray(feat.context)) {
                for (const c of feat.context) {
                  const id = c.id || '';
                  if (!colonia && (id.startsWith('neighborhood') || id.startsWith('locality'))) colonia = c.text || '';
                }
              }
            } else {
              address = `Ubicación ${finalLat.toFixed(5)}, ${finalLng.toFixed(5)}`;
            }
            onChange({ address, lat: finalLat, lng: finalLng, colonia });
          } catch {
            onChange({ address: `Ubicación ${finalLat.toFixed(5)}, ${finalLng.toFixed(5)}`, lat: finalLat, lng: finalLng, colonia: '' });
          }
        };

        marker.on('dragend', () => {
          const p = marker.getLngLat();
          reverseAndReport(p.lng, p.lat);
        });
        await reverseAndReport(lng, lat);
      };

      // Click en cualquier parte del mapa: coloca/mueve el pin ahí
      map.on('click', (e) => {
        placePinAt(e.lngLat.lng, e.lngLat.lat);
      });
      // Cursor pointer sobre el mapa para indicar que se puede clicar
      map.getCanvas().style.cursor = 'crosshair';

      const geocoder = new MapboxGeocoder({
        accessToken: MAPBOX_TOKEN,
        mapboxgl,
        language: 'es',
        countries: 'mx',
        bbox: DEFAULT_BBOX,
        types: 'address,poi,neighborhood,locality',
        proximity: { longitude: center[0], latitude: center[1] },
        limit: 6,
        minLength: 3,
        placeholder: 'Buscar colonia y calle…',
        marker: false,
        render: (item) => {
          const escape = (s) => String(s || '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
          const title = item.address ? `${item.text} ${item.address}` : item.text;
          let colonia = ''; let city = ''; let postcode = '';
          const pt = Array.isArray(item.place_type) ? item.place_type : [];
          if (pt.includes('neighborhood') || pt.includes('locality')) colonia = item.text || '';
          if (Array.isArray(item.context)) {
            for (const c of item.context) {
              const id = c.id || '';
              if (!colonia && (id.startsWith('neighborhood') || id.startsWith('locality'))) colonia = c.text || '';
              if (!city && (id.startsWith('place') || id.startsWith('district'))) city = c.text || '';
              if (!postcode && id.startsWith('postcode')) postcode = c.text || '';
            }
          }
          const sub = [];
          if (colonia) sub.push(`<strong style="color:#7c3aed">${escape(colonia)}</strong>`);
          if (postcode) sub.push(escape(postcode));
          if (city) sub.push(escape(city));
          return `<div style="padding:2px 0"><div style="font-weight:600;color:#0f172a">${escape(title)}</div>${sub.length?`<div style="font-size:12px;color:#64748b;margin-top:2px">${sub.join(' · ')}</div>`:''}</div>`;
        },
      });
      geocoderRef.current = geocoder;

      geocoder.on('result', (evt) => {
        const feat = evt.result;
        if (!feat?.geometry?.coordinates) return;
        const [lng, lat] = feat.geometry.coordinates;
        placePinAt(lng, lat);
        map.flyTo({ center: [lng, lat], zoom: 15, duration: 700 });
      });

      if (geocoderContainer.current) {
        geocoderContainer.current.innerHTML = '';
        geocoderContainer.current.appendChild(geocoder.onAdd(map));
      }
    };

    // Espera a que el contenedor tenga dimensiones reales antes de crear el mapa.
    // Esto evita el "canvas en blanco" cuando el picker se renderiza dentro de un
    // panel colapsable/dinámico (ej: carrito del POS).
    const tryInit = () => {
      const el = mapContainer.current;
      if (!el) return false;
      const w = el.clientWidth || 0;
      const h = el.clientHeight || 0;
      if (w < 10 || h < 10) return false;

      initedRef.current = true;
      mapboxgl.accessToken = MAPBOX_TOKEN;
      const map = new mapboxgl.Map({
        container: el,
        style: 'mapbox://styles/mapbox/streets-v12',
        center,
        zoom: 12,
      });
      map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');
      mapRef.current = map;

      // Resize observer + resizes agendados por si el contenedor cambia después
      const ro = new ResizeObserver(() => {
        try { map.resize(); } catch { /* noop */ }
      });
      ro.observe(el);
      map._picker_ro = ro;
      setTimeout(() => { try { map.resize(); } catch { /* noop */ } }, 300);
      setTimeout(() => { try { map.resize(); } catch { /* noop */ } }, 1200);

      setupGeocoder(map);
      return true;
    };

    if (tryInit()) {
      return () => {
        if (mapRef.current) {
          try { mapRef.current._picker_ro?.disconnect(); mapRef.current.remove(); } catch { /* noop */ }
          mapRef.current = null;
        }
        initedRef.current = false; // permitir re-init en StrictMode / re-mount
      };
    }
    let attempts = 0;
    const iv = setInterval(() => {
      attempts += 1;
      if (tryInit() || attempts > 50) clearInterval(iv);
    }, 100);
    return () => {
      clearInterval(iv);
      if (mapRef.current) {
        try { mapRef.current._picker_ro?.disconnect(); mapRef.current.remove(); } catch { /* noop */ }
        mapRef.current = null;
      }
      initedRef.current = false; // permitir re-init en StrictMode / re-mount
    };
    // eslint-disable-next-line
  }, []);

  // Si el value entra con lat/lng (ej al reabrir dialog), pintar marker
  useEffect(() => {
    if (!mapRef.current || !value?.lat || !value?.lng) return;
    if (markerRef.current) markerRef.current.remove();
    const el = document.createElement('div');
    el.style.cssText = 'width:30px;height:30px;background:#2563eb;border:3px solid white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-size:12px;';
    el.textContent = '📍';
    markerRef.current = new mapboxgl.Marker({ element: el }).setLngLat([value.lng, value.lat]).addTo(mapRef.current);
    mapRef.current.flyTo({ center: [value.lng, value.lat], zoom: 15, duration: 500 });
  }, [value?.lat, value?.lng]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="border border-rose-300 bg-rose-50 rounded p-3 text-xs text-rose-700">
        ⚠️ Falta REACT_APP_MAPBOX_TOKEN. El pin de dirección no está disponible.
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded overflow-hidden bg-white">
      <style>{`
        .address-picker .mapboxgl-ctrl-geocoder {
          width: 100% !important;
          max-width: 100% !important;
          box-shadow: none !important;
          border-bottom: 1px solid #e2e8f0;
        }
        .address-picker .mapboxgl-map,
        .address-picker .mapboxgl-canvas-container,
        .address-picker .mapboxgl-canvas {
          width: 100% !important;
          height: 100% !important;
        }
      `}</style>
      <div className="address-picker">
        <div ref={geocoderContainer} />
        <div
          ref={mapContainer}
          style={{ width: '100%', height: `${height}px`, minHeight: `${height}px`, position: 'relative', background: '#f1f5f9' }}
        />
        {value?.address && (
          <div className="p-2 bg-emerald-50 border-t border-emerald-200 text-xs text-emerald-800 flex items-start gap-2">
            <span>✅</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{value.address}</p>
              {value.colonia && <p className="text-emerald-600">Colonia: {value.colonia}</p>}
            </div>
            <button
              type="button"
              className="text-emerald-700 hover:text-rose-600 font-bold"
              onClick={() => {
                if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
                if (geocoderRef.current) { try { geocoderRef.current.clear(); } catch { /* noop */ } }
                onChange(null);
              }}
              title="Quitar pin"
            >×</button>
          </div>
        )}
      </div>
    </div>
  );
}
